import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { Loader2, XCircle } from 'lucide-react';

const PageLayout = ({ children }) => (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-8 md:p-12">
            {children}
        </div>
    </div>
);

const text = {
    en: {
        redirecting: 'Redirecting to identity verification...',
        invalidTitle: 'Link Invalid',
        invalidDefault: 'This link is invalid or has expired.',
        noToken: 'No verification token provided. This link is invalid.',
        sessionError: 'Could not start the verification session. Please try again later.',
    },
    es: {
        redirecting: 'Redirigiendo a la verificación de identidad...',
        invalidTitle: 'Enlace Inválido',
        invalidDefault: 'Este enlace es inválido o ha expirado.',
        noToken: 'No se proporcionó un token de verificación. Este enlace es inválido.',
        sessionError: 'No se pudo iniciar la sesión de verificación. Por favor, inténtelo de nuevo más tarde.',
    },
};

const EkycPage = () => {
    const [searchParams] = useSearchParams();

    const [status, setStatus] = useState('redirecting'); // redirecting, invalid
    const [error, setError] = useState('');

    const token = searchParams.get('token');
    const lang = searchParams.get('lang') === 'es' ? 'es' : 'en';
    const t = text[lang];

    const startEkyc = useCallback(async () => {
        if (!token) {
            setError(t.noToken);
            setStatus('invalid');
            return;
        }

        try {
            // Step 1: Verify token is still valid
            await api.post(`/api/ekyc/verify-token`, { token });

            // Step 2: Create ComplyCube session with language
            const response = await api.post(`/api/ekyc/create-session`, {
                token,
                language: lang === 'es' ? 'es' : undefined,
            });

            const sessionUrl = response.data.url;
            if (!sessionUrl) {
                throw new Error('No session URL returned');
            }

            // Step 3: Redirect to ComplyCube
            window.location.href = sessionUrl;
        } catch (err) {
            console.error('eKYC redirect failed:', err);
            const serverMsg = err.response?.data?.message;
            if (err.response?.status === 404) {
                setError(serverMsg || t.invalidDefault);
            } else {
                setError(serverMsg || t.sessionError);
            }
            setStatus('invalid');
        }
    }, [token, lang, t]);

    useEffect(() => {
        startEkyc();
    }, [startEkyc]);

    if (status === 'invalid') {
        return (
            <PageLayout>
                <div className="flex flex-col items-center justify-center text-center py-8">
                    <XCircle className="text-red-500" size={48} />
                    <h2 className="mt-4 text-2xl font-bold text-gray-800">{t.invalidTitle}</h2>
                    <p className="mt-2 text-gray-600">{error}</p>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <div className="flex flex-col items-center justify-center text-center py-12">
                <Loader2 className="animate-spin text-indigo-600" size={48} />
                <p className="mt-4 text-lg font-medium text-gray-700">{t.redirecting}</p>
            </div>
        </PageLayout>
    );
};

export default EkycPage;
